import Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import { useCallback, useMemo } from 'react';
import { Circle, Group, Rect, RegularPolygon } from 'react-konva';
import { useStudio, type StudioItem } from '../../context';
import { StudioImage, StudioText, StudioVideo } from './';

export interface StudioBaseItemProps {
  item: StudioItem;
  /** Display-only override for a device-aware item. Never written back. */
  previewText?: string;
}

export const StudioBaseItem = ({ item, previewText }: StudioBaseItemProps) => {
  const { selectItems, selectedItems, updateItems } = useStudio();

  const onDragEnd = useCallback(
    (event: KonvaEventObject<DragEvent>) => {
      updateItems([
        { id: item.id, x: Math.round(event.target.x()), y: Math.round(event.target.y()) },
      ]);
      if (!selectedItems.some((s) => s.id === item.id)) {
        selectItems([item.id]);
      }
    },
    [item, selectItems, selectedItems, updateItems],
  );

  const onTransform = useCallback((event: KonvaEventObject<Event>) => {
    // reset height and width instead of relying on scale, makes
    // certain attributes more predicatable (border, font size, etc)
    event.target.setAttrs({
      ...(event.target instanceof Konva.Text && {
        fontSize: Math.abs(event.target.fontSize() * event.target.scaleY()),
      }),
      ...(!(event.target instanceof Konva.Text) && {
        height: Math.abs(event.target.height() * event.target.scaleY()),
      }),
      scaleX: 1,
      scaleY: 1,
      width: Math.abs(event.target.width() * event.target.scaleX()),
    });
  }, []);

  const onTransformEnd = useCallback(
    (event: KonvaEventObject<Event>) => {
      updateItems([
        {
          id: item.id,
          ...(event.target instanceof Konva.Text && {
            fontSize: Math.round(Math.abs(event.target.fontSize())),
          }),
          ...(!(event.target instanceof Konva.Text) && {
            height: Math.round(Math.abs(event.target.height())),
          }),
          ...(event.target instanceof Konva.RegularPolygon && {
            radius: Math.round(Math.abs(event.target.radius())),
          }),
          rotation: Math.round(event.target.rotation()),
          scaleX: event.target.scaleX(),
          scaleY: event.target.scaleY(),
          width: Math.round(Math.abs(event.target.width())),
          x: Math.round(event.target.x()),
          y: Math.round(event.target.y()),
        },
      ]);
    },
    [item, updateItems],
  );

  const editProps = useMemo(
    () => ({
      draggable: !item.locked,
      onDragEnd: item.locked ? undefined : onDragEnd,
      onTransform: item.locked ? undefined : onTransform,
      onTransformEnd: item.locked ? undefined : onTransformEnd,
    }),
    [item.locked, onDragEnd, onTransform, onTransformEnd],
  );

  return (
    <Group>
      {item.type === 'circle' && <Circle {...item} {...editProps} />}
      {item.type === 'image' && <StudioImage item={item} {...editProps} />}
      {item.type === 'rect' && <Rect {...item} {...editProps} />}
      {item.type === 'triangle' && <RegularPolygon {...item} {...editProps} />}
      {item.type === 'text' && (
        <StudioText
          {...item}
          {...editProps}
          locked={!!item.__dataItemId}
          previewText={previewText}
        />
      )}
      {item.type === 'video' && <StudioVideo item={item} {...editProps} />}
    </Group>
  );
};
